import {z} from 'zod';

import {isNil} from 'ramda';

import {
  BasePriceSourceSchema,
  PurchasePriceDefSupplierSourceSchema,
  RecommendedPriceSourceSchema,
  SalesPriceCalculationSchema,
  StockManagementTypeSchema,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

export const articleDetailFormSchema = z
  .object({
    generalInformation: z.object({
      basicInformation: z.object({
        name: z.string(),
        description: z.string().nullish(),
        manufacturerNumber: z.string(),
        manufacturerId: z.string(),
        supplierId: z.string(),
        supplierDiscountGroupId: z.string().nullish(),
        warehouseId: z.string(),
        storageLocationId: z.string().nullish(),
        marketingCode: z.string().nullish(),
        workItemGroupId: z.string().nullish(),
        vatType: z.string(),
      }),
      units: z.object({
        handlingUnit: z.string(),
        dispensingUnit: z.number(),
      }),
    }),
    prices: z.object({
      pricesSettings: z.object({
        avgPurchasePriceWithoutVat: z.number().nullish(),
        lastPurchasePriceWithoutVat: z.number().nullish(),
        recommendedPurchasePriceWithoutVat: z.number().nullish(),
        purchasePriceDefSupplierWithoutVat: z.number().nullish(),
        avgPurchasePriceWithVat: z.number().nullish(),
        lastPurchasePriceWithVat: z.number().nullish(),
        recommendedPurchasePriceWithVat: z.number().nullish(),
        purchasePriceDefSupplierWithVat: z.number().nullish(),
        salesPriceCalculation: SalesPriceCalculationSchema,
        basePriceSource: BasePriceSourceSchema,
        unitSalesPriceWithVat: z.boolean(),
        recommendedPriceSource: RecommendedPriceSourceSchema.nullish(),
        purchasePriceDefSupplierSource: PurchasePriceDefSupplierSourceSchema.nullish(),
      }),
      dispensingPrices: z.object({
        basePriceWithoutVat: z.number().nullish(),
        basePriceWithVat: z.number().nullish(),
        saleBasePriceWithoutVat: z.number().nullish(),
        saleBasePriceWithVat: z.number().nullish(),
        saleBaseProfit: z.number().nullish(),
        saleBaseMarginPercent: z.number().nullish(),
        saleBaseMarkUpPercent: z.number().nullish(),
        warrantyPriceWithoutVat: z.number().nullish(),
        warrantyPriceWithVat: z.number().nullish(),
        warrantyProfit: z.number().nullish(),
        warrantyMarginPercent: z.number().nullish(),
        warrantyMarkUpPercent: z.number().nullish(),
      }),
    }),
    stockManagement: z.object({
      stockManagementType: StockManagementTypeSchema,
      stockManagementMinQuantity: z.number().nullish(),
      stockManagementMaxQuantity: z.number().nullish(),
      isOneTimeSale: z.boolean(),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.stockManagement.stockManagementType === 'MIN_MAX') {
      if (isNil(data.stockManagement.stockManagementMinQuantity)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.warehouse.validations.valueCannotBeEmpty'),
          path: ['stockManagement', 'stockManagementMinQuantity'],
        });
      }
      if (isNil(data.stockManagement.stockManagementMaxQuantity)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('entity.warehouse.validations.valueCannotBeEmpty'),
          path: ['stockManagement', 'stockManagementMaxQuantity'],
        });
      }
    }
  });

export type ArticleDetailForm = z.infer<typeof articleDetailFormSchema>;
